<template>
	<div class="card shadow-sm mt-4">
		<div class="card-header d-flex justify-content-between align-items-center">
			<div>
				<h4 class="h5 mb-0">Source aliases</h4>
				<p class="text-muted mb-0 small">
					The names the source systems use for this member. An import attributes
					work through these, so they survive a display-name change.
				</p>
			</div>
			<button
				class="btn btn-sm btn-outline-secondary"
				:disabled="busy"
				@click="openMerge"
			>
				Merge into…
			</button>
		</div>
		<div class="card-body">
			<div v-if="error" class="alert alert-danger" role="alert">
				{{ error }}
			</div>

			<div v-if="loading" class="text-muted">loading…</div>
			<template v-else>
				<p v-if="aliases.length === 0" class="text-muted">
					This member has no source aliases yet.
				</p>
				<div v-else class="table-responsive mb-3">
					<table class="table table-sm align-middle mb-0">
						<thead>
							<tr>
								<th scope="col">Source</th>
								<th scope="col">Value</th>
								<th scope="col">Origin</th>
								<th scope="col">Added</th>
								<th scope="col" class="text-end"></th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="alias in aliases" :key="alias.id">
								<td>
									<span class="badge text-bg-light text-uppercase">
										{{ sourceLabel(alias.sourceSystem) }}
									</span>
								</td>
								<td>
									<code>{{ alias.value }}</code>
								</td>
								<td>
									<span
										class="badge"
										:class="
											alias.origin === 'admin'
												? 'text-bg-primary'
												: 'text-bg-secondary'
										"
									>
										{{ alias.origin }}
									</span>
								</td>
								<td><TimeRelativeWithTooltip :time="alias.created" /></td>
								<td class="text-end">
									<button
										class="btn btn-sm btn-outline-danger"
										:disabled="busy"
										@click="remove(alias)"
									>
										Delete
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<form class="row g-2 align-items-end" @submit.prevent="add">
					<div class="col-sm-3">
						<label class="form-label" for="aliasSource">Source</label>
						<select id="aliasSource" v-model="newSource" class="form-select">
							<option value="fieldseeker">FieldSeeker</option>
							<option value="mapvision">MapVision</option>
						</select>
					</div>
					<div class="col-sm-6">
						<label class="form-label" for="aliasValue">Name</label>
						<input
							id="aliasValue"
							v-model="newValue"
							type="text"
							class="form-control"
							placeholder="The name the source uses"
						/>
					</div>
					<div class="col-sm-3">
						<button
							class="btn btn-primary w-100"
							type="submit"
							:disabled="!newValue.trim() || busy"
						>
							Add
						</button>
					</div>
				</form>
			</template>
		</div>

		<!-- Merge this member into another -->
		<div v-if="merging" class="modal d-block" tabindex="-1" role="dialog">
			<div class="modal-dialog modal-dialog-centered" role="document">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Merge this member into…</h5>
						<button
							type="button"
							class="btn-close"
							aria-label="Close"
							@click="closeMerge"
						></button>
					</div>
					<div class="modal-body">
						<p class="text-muted small">
							The aliases and every credited record move to the member you pick,
							and this member is retired. There is no unmerge.
						</p>
						<UserSelector
							v-model="mergeTarget"
							:all-organizations="allOrganizations"
							placeholder="Search members…"
						/>
						<div v-if="preview" class="alert alert-warning mt-3 mb-0">
							This moves <strong>{{ preview.aliases }}</strong> alias(es) and
							<strong>{{ preview.records.toLocaleString() }}</strong> credited
							record(s) to {{ mergeTarget?.display_name }}.
						</div>
					</div>
					<div class="modal-footer">
						<button class="btn btn-outline-secondary" @click="closeMerge">
							Cancel
						</button>
						<button
							class="btn btn-danger"
							:disabled="!mergeTarget || !preview || busy"
							@click="confirmMerge"
						>
							Merge
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import TimeRelativeWithTooltip from "@/components/TimeRelativeWithTooltip.vue";
import UserSelector from "@/components/UserSelector.vue";
import { apiClient } from "@/client";
import type { User, UserAlias, UserMergeCounts } from "@/type/api";

interface Props {
	userId: number;
	/** Offer members of every district as a merge target. An administrator
	 * editing a member from another district needs it; a district's own
	 * surface does not. */
	allOrganizations?: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
	merged: [intoUserId: number, counts: UserMergeCounts];
}>();

const aliases = ref<UserAlias[]>([]);
const loading = ref(true);
const busy = ref(false);
const error = ref("");

const newSource = ref("fieldseeker");
const newValue = ref("");

const merging = ref(false);
const mergeTarget = ref<User | null>(null);
const preview = ref<UserMergeCounts | null>(null);

function sourceLabel(system: string): string {
	return system === "mapvision" ? "MapVision" : "FieldSeeker";
}

async function load() {
	loading.value = true;
	error.value = "";
	try {
		aliases.value = await apiClient.JSONGet<UserAlias[]>(
			`/api/user/${props.userId}/alias`,
		);
	} catch (e: any) {
		error.value = e?.message || "Failed to load this member's aliases.";
	} finally {
		loading.value = false;
	}
}

async function add() {
	const value = newValue.value.trim();
	if (!value) return;
	busy.value = true;
	error.value = "";
	try {
		await apiClient.JSONPost<UserAlias>(`/api/user/${props.userId}/alias`, {
			sourceSystem: newSource.value,
			rawValue: value,
		});
		newValue.value = "";
		await load();
	} catch (e: any) {
		error.value = e?.message || "Failed to add the alias.";
	} finally {
		busy.value = false;
	}
}

async function remove(alias: UserAlias) {
	if (
		!window.confirm(
			`Remove “${alias.value}”? The value becomes unmapped and the next import ` +
				"resolves it again — use the technician-name card to retire a name for good.",
		)
	) {
		return;
	}
	busy.value = true;
	error.value = "";
	try {
		await apiClient.JSONDelete(`/api/user/${props.userId}/alias/${alias.id}`);
		await load();
	} catch (e: any) {
		error.value = e?.message || "Failed to remove the alias.";
	} finally {
		busy.value = false;
	}
}

function openMerge() {
	mergeTarget.value = null;
	preview.value = null;
	merging.value = true;
}

function closeMerge() {
	merging.value = false;
	mergeTarget.value = null;
	preview.value = null;
}

// The preview is what the confirm button promises, so it is fetched whenever the
// target changes rather than guessed from counts held on the page.
watch(mergeTarget, async (target) => {
	preview.value = null;
	if (!target) return;
	try {
		preview.value = await apiClient.JSONGet<UserMergeCounts>(
			`/api/user/${props.userId}/merge/preview/${target.id}`,
		);
	} catch (e: any) {
		error.value = e?.message || "Failed to preview the merge.";
	}
});

async function confirmMerge() {
	if (!mergeTarget.value || !preview.value) return;
	busy.value = true;
	error.value = "";
	try {
		const counts = await apiClient.JSONPost<UserMergeCounts>(
			`/api/user/${props.userId}/merge`,
			{ intoUserId: mergeTarget.value.id },
		);
		const into = mergeTarget.value.id;
		closeMerge();
		emit("merged", into, counts);
	} catch (e: any) {
		error.value = e?.message || "Failed to merge the members.";
	} finally {
		busy.value = false;
	}
}

onMounted(load);
watch(() => props.userId, load);
</script>

<style scoped>
/* The modal markup is inline rather than bootstrap's JS plugin: the SPA drives
 * it with v-if, so the backdrop and z-index are all it needs. */
.modal.d-block {
	background: rgba(0, 0, 0, 0.4);
}
</style>
